<template>
    <div>
        <div style="background-color: white; border-radius: 20px; padding: 20px 10px 0 10px; margin-top: 25px">
            <v-text-field
                ref="ACU_COMPANY"
                v-model.trim="endorsementData.ACU_COMPANY"
                :rules="rules.required"
                clearable
                label="Company name"
                outlined
                dense
                color="teal"
            />
            <v-autocomplete
                ref="selectedClinic"
                v-model.trim="selectedClinic"
                outlined
                :rules="rules.required"
                clearable
                :items="viewAccountClinicsData && viewAccountClinicsData.data"
                item-text="MSU_N"
                dense
                label="Select clinic"
                :return-object="true"
                color="teal"
                @change="updateClinicData"
            />
            <v-text-field
                ref="acustartdate"
                v-model="endorsementData.ACU_START"
                type="date"
                label="ACU Start Date"
                color="teal"
                :rules="rules.required.concat([
                    (v)=> validateAcuStart(v, endorsementData.ACU_END),
                    (v)=> validateAcuLimitLetterEnd(v, endorsementData.ACU_LETTER_LIMIT_START)
                ])"
                dense
            />
            <v-text-field
                ref="acuenddate"
                v-model="endorsementData.ACU_END"
                type="date"
                label="ACU End Date"
                color="teal"
                :rules="rules.required.concat([
                    (v)=> validateAcuEnd(v, endorsementData.ACU_START),
                    (v)=> validateAcuLimitLetterStart(v, endorsementData.ACU_LETTER_LIMIT_END)
                ])"
            />
        </div>
        <v-switch
            v-model="endorsementData.CREATE_END"
            inset
            hide-details
            label="Endorsement"
            color="teal"
            style="margin: 30px 0 20px 10px;"
        />
        <v-expand-transition>
            <div
                v-show="endorsementData.CREATE_END"
                style="background-color: white; border-radius: 20px; padding: 0 10px;"
            >
                <v-row>
                    <v-col cols="3">
                        <v-combobox
                            ref="TO_PREFIX_END"
                            v-model.trim="endorsementData.TO_PREFIX_END"
                            :rules="requireCreatedEndFields"
                            :items="honorifics"
                            item-text="title"
                            item-value="title"
                            label="Name prefix"
                            dense
                            :menu-props="{ maxHeight: '300px' }"
                            style="width: 100%; display: inline-block; margin-right: 10px;"
                            clearable
                            :return-object="false"
                            color="teal"
                            outlined
                        >
                            <template #item="{ item }">
                                <template v-if="item.header">
                                    <v-subheader>{{ item.header }}</v-subheader>
                                </template>
                                <template v-else>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </template>
                        </v-combobox>
                    </v-col>
                    <v-col :cols="undefined">
                        <v-text-field
                            ref="TO_FNAME_END"
                            v-model.trim="endorsementData.TO_FNAME_END"
                            dense
                            :rules="requireCreatedEndFields"
                            clearable
                            label="First name"
                            style="width: 100%; display: inline-block; margin-right: 10px;"
                            color="teal"
                            outlined
                        />
                    </v-col>
                    <v-col :cols="undefined">
                        <v-text-field
                            ref="TO_SNAME_END"
                            v-model.trim="endorsementData.TO_SNAME_END"
                            dense
                            :rules="requireCreatedEndFields"
                            clearable
                            label="Last name"
                            style="width: 100%; display: inline-block; margin-right: 10px;"
                            color="teal"
                            outlined
                        />
                    </v-col>
                    <v-col :cols="undefined">
                        <v-combobox
                            ref="TO_SUFFIX_END"
                            v-model.trim="endorsementData.TO_SUFFIX_END"
                            dense
                            clearable
                            label="Suffix"
                            style="width: 100%; display: inline-block; margin-right: 10px;"
                            color="teal"
                            :items="suffixes"
                            item-text="suffix"
                            item-value="suffix"
                            :return-object="false"
                            outlined
                            :menu-props="{ maxHeight: '300px' }"
                        />
                    </v-col>
                </v-row>
                <v-combobox
                    ref="TO_DESIGNATION_END"
                    v-model.trim="endorsementData.TO_DESIGNATION_END"
                    :items="designations"
                    item-text="title"
                    item-value="title"
                    label="Select designation"
                    dense
                    clearable
                    :return-object="false"
                    :menu-props="{ maxHeight: '300px' }"
                    style="width: 100%;"
                    color="teal"
                    outlined
                >
                    <template #item="{ item }">
                        <v-list-item-content><v-list-item-title>{{ item.title }}</v-list-item-title></v-list-item-content>
                    </template>
                </v-combobox>
                <p>Please choose which info will show on the members' list table:</p>
                <v-row>
                    <v-col :cols="undefined">
                        <div
                            v-for="(itemLeft, index) in leftColumnEndorsementIncludedInfo"
                            :key="index"
                        >
                            <v-checkbox
                                v-model="itemLeft.included"
                                :label="itemLeft.title"
                                color="teal"
                                hide-details
                            />
                        </div>
                    </v-col>
                    <v-col :cols="undefined">
                        <div
                            v-for="(itemRight, index) in rightColumnEndorsementIncludedInfo"
                            :key="index"
                        >
                            <v-checkbox
                                v-model="itemRight.included"
                                :label="itemRight.title"
                                color="teal"
                                hide-details
                            />
                        </div>
                    </v-col>
                </v-row>
            </div>
        </v-expand-transition>
        <v-switch
            v-model="endorsementData.CREATE_CON"
            inset
            hide-details
            label="Confirmation"
            color="teal"
            style="margin: 40px 0 10px 10px;"
        />
        <v-expand-transition>
            <div
                v-show="endorsementData.CREATE_CON"
                style="background-color: white; border-radius: 20px; padding: 0 10px;"
            >
                <v-checkbox
                    v-model="endorsementData.IS_CON_ADDR_MEMBER"
                    label="The addressee is the HMI member who will undergo ACU."
                    style="padding: 20px 0; margin: 0"
                    color="teal"
                    hide-details
                />
                <v-row>
                    <v-col cols="3">
                        <v-combobox
                            ref="TO_PREFIX_CON"
                            v-model.trim="endorsementData.TO_PREFIX_CON"
                            :rules="requireCreatedConFields"
                            :items="honorifics"
                            item-text="title"
                            item-value="title"
                            label="Name prefix"
                            dense
                            :menu-props="{ maxHeight: '300px' }"
                            style="width: 100%; display: inline-block; margin-right: 10px;"
                            clearable
                            :return-object="false"
                            color="teal"
                            outlined
                        >
                            <template #item="{ item }">
                                <template v-if="item.header">
                                    <v-subheader>{{ item.header }}</v-subheader>
                                </template>
                                <template v-else>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </template>
                        </v-combobox>
                    </v-col>
                    <v-col :cols="undefined">
                        <v-text-field
                            ref="TO_FNAME_CON"
                            v-model.trim="endorsementData.TO_FNAME_CON"
                            dense
                            :rules="requireCreatedConFields"
                            clearable
                            label="First name"
                            style="width: 100%; display: inline-block; margin-right: 10px;"
                            color="teal"
                            outlined
                        />
                    </v-col>
                    <v-col :cols="undefined">
                        <v-text-field
                            ref="TO_SNAME_CON"
                            v-model.trim="endorsementData.TO_SNAME_CON"
                            dense
                            :rules="requireCreatedConFields"
                            clearable
                            label="Last name"
                            style="width: 100%; display: inline-block; margin-right: 10px;"
                            color="teal"
                            outlined
                        />
                    </v-col>
                    <v-col :cols="undefined">
                        <v-combobox
                            ref="TO_SUFFIX_CON"
                            v-model.trim="endorsementData.TO_SUFFIX_CON"
                            dense
                            clearable
                            label="Suffix"
                            style="width: 100%; display: inline-block; margin-right: 10px;"
                            color="teal"
                            :items="suffixes"
                            item-text="suffix"
                            item-value="suffix"
                            :return-object="false"
                            outlined
                            :menu-props="{ maxHeight: '300px' }"
                        />
                    </v-col>
                </v-row>
                <v-combobox
                    ref="TO_DESIGNATION_CON"
                    v-model.trim="endorsementData.TO_DESIGNATION_CON"
                    :items="designations"
                    item-text="title"
                    item-value="title"
                    label="Select designation"
                    dense
                    clearable
                    :return-object="false"
                    :menu-props="{ maxHeight: '300px' }"
                    style="width: 100%;"
                    color="teal"
                    outlined
                >
                    <template #item="{ item }">
                        <v-list-item-content><v-list-item-title>{{ item.title }}</v-list-item-title></v-list-item-content>
                    </template>
                </v-combobox>
                <v-textarea
                    v-model.trim="endorsementData.ACU_SCHEDULE"
                    label="ACU Schedule"
                    outlined
                    color="teal"
                />
                <v-text-field
                    ref="ACU_CONTACT_PERSON"
                    v-model.trim="endorsementData.ACU_CONTACT_PERSON"
                    clearable
                    label="ACU Contact Person"
                    outlined
                    dense
                    color="teal"
                />
                <v-text-field
                    ref="ACU_CLINIC_TELNO"
                    v-model.trim="endorsementData.ACU_CLINIC_TELNO"
                    clearable
                    label="ACU Contact No"
                    outlined
                    dense
                    color="teal"
                />
                <v-textarea
                    ref="CONFIRMATION_ADDITIONAL_CLAUSE"
                    v-model.trim="endorsementData.CONFIRMATION_ADDITIONAL_CLAUSE"
                    clearable
                    label="Additional Coverage Clause"
                    outlined
                    dense
                    color="teal"
                />
                <p>Please choose which info will show on the members' list table:</p>
                <v-row>
                    <v-col :cols="undefined">
                        <div
                            v-for="(itemLeft, index2) in leftColumnConfirmationIncludedInfo"
                            :key="index2"
                        >
                            <v-checkbox
                                v-model="itemLeft.included"
                                :label="itemLeft.title"
                                color="teal"
                                hide-details
                            />
                        </div>
                    </v-col>
                    <v-col :cols="undefined">
                        <div
                            v-for="(itemRight, index2) in rightColumnConfirmationIncludedInfo"
                            :key="index2"
                        >
                            <v-checkbox
                                v-model="itemRight.included"
                                :label="itemRight.title"
                                color="teal"
                                hide-details
                            />
                        </div>
                    </v-col>
                </v-row>
            </div>
        </v-expand-transition>
        <v-btn
            small
            color="teal"
            style="margin: 25px 0 20px 10px; text-transform: none;"
            class="white--text"
            @click="$emit('showSelectedMember')"
        >
            <v-icon small>
                {{ mdiEye }}
            </v-icon>&nbsp;Show selected members
        </v-btn>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AdminAPI from '../../../../../../store/APIs/AdminAPI/AdminAPI'
import { moment } from '../../../../../../utils/date-shim'
import {
    mdiEye
} from '@mdi/js'

export default {
    props: ['endorsementData'],
    data: () => ({
        mdiEye,
        selectedClinic: null,
        honorifics: [
            { header: 'General Titles' },
            { title: 'Mr.' }, { title: 'Mrs.' }, { title: 'Ms.' }, { title: 'Miss' },

            { header: 'Professional Titles' },
            { title: 'Dr.' }, { title: 'Atty.' }, { title: 'Engr.' }, { title: 'CPA.' }, { title: 'Prof.' },

            { header: 'Polite Titles' },
            { title: 'Sir' }, { title: 'Ma\'am' }, { title: 'Madam' }
        ],
        designations: [
            { title: 'Manager' },
            { title: 'Senior Manager' },
            { title: 'Area Manager' },
            { title: 'Branch Manager' },
            { title: 'Operations Manager' },
            { title: 'Department Head' },
            { title: 'Unit Head' },
            { title: 'Director' },
            { title: 'General Manager' },
            { title: 'Vice President (VP)' },
            { title: 'Assistant Vice President (AVP)' },
            { title: 'Senior Vice President (SVP)' },
            { title: 'President' },
            { title: 'Chief Executive Officer (CEO)' },
            { title: 'Chief Operating Officer (COO)' },
            { title: 'Chief Finance Officer (CFO)' },
            { title: 'Chief Technology Officer (CTO)' },
            { title: 'Medical Director' },
            { title: 'Chief Medical Officer' },
            { title: 'Hospital Administrator' },
            { title: 'Chief Nurse' },
            { title: 'Senior Consultant' }
        ],
        suffixes: [
            { suffix: 'Sr.' },
            { suffix: 'Jr.' },
            { suffix:  'I' } ,
            { suffix:  'II' },
            { suffix:  'III' },
            { suffix:  'IV' },
            { suffix:  'V' }
        ],
        rules: {
            required: [
                (v)=> !!v || 'This field is required'
            ]
        }
    }),
    computed: {
        ...mapGetters(['viewAccountClinicsData']),
        requireCreatedEndFields() {
            return this.endorsementData.CREATE_END ? this.rules.required : [() => true]
        },
        requireCreatedConFields() {
            return this.endorsementData.CREATE_CON ? this.rules.required : [() => true]
        },
        leftColumnEndorsementIncludedInfo() {
            return this.endorsementData.INCLUDED_INFO_END.filter((_, i) => i % 2 === 0)
        },
        rightColumnEndorsementIncludedInfo() {
            return this.endorsementData.INCLUDED_INFO_END.filter((_, i) => i % 2 !== 0)
        },
        leftColumnConfirmationIncludedInfo() {
            return this.endorsementData.INCLUDED_INFO_CON.filter((_, i) => i % 2 === 0)
        },
        rightColumnConfirmationIncludedInfo() {
            return this.endorsementData.INCLUDED_INFO_CON.filter((_, i) => i % 2 !== 0)
        }
    },
    watch: {
        'endorsementData.CREATE_END'(newVal) {
            if (!newVal) {
                this.$nextTick(() => {
                    if (this.$refs) {
                        if (this.$refs.TO_PREFIX_END) this.$refs.TO_PREFIX_END.reset()
                        if (this.$refs.TO_FNAME_END) this.$refs.TO_FNAME_END.reset()
                        if (this.$refs.TO_SNAME_END) this.$refs.TO_SNAME_END.reset()
                        if (this.$refs.TO_SUFFIX_END) this.$refs.TO_SUFFIX_END.reset()
                        if (this.$refs.TO_DESIGNATION_END) this.$refs.TO_DESIGNATION_END.reset()
                    }
                })
            }
            this.prefillFieldsWithSuggestions()
        },
        'endorsementData.CREATE_CON'(newVal) {
            if (!newVal) {
                this.$nextTick(() => {
                    if (this.$refs) {
                        if (this.$refs.TO_PREFIX_CON) this.$refs.TO_PREFIX_CON.reset()
                        if (this.$refs.TO_FNAME_CON) this.$refs.TO_FNAME_CON.reset()
                        if (this.$refs.TO_SNAME_CON) this.$refs.TO_SNAME_CON.reset()
                        if (this.$refs.TO_SUFFIX_CON) this.$refs.TO_SUFFIX_CON.reset()
                        if (this.$refs.TO_DESIGNATION_CON) this.$refs.TO_DESIGNATION_CON.reset()
                    }
                })
            }
            this.prefillFieldsWithSuggestions()
        },
        'selectedClinic' () {
            this.prefillFieldsWithSuggestions()
        },
        'endorsementData.ACU_START' () {
            this.$nextTick(() => {
                this.$refs['acustartdate'].validate()
                this.$refs['acuenddate'].validate()
            })
        },
        'endorsementData.ACU_END' () {
            this.$nextTick(() => {
                this.$refs['acustartdate'].validate()
                this.$refs['acuenddate'].validate()
            })
        }
    },
    methods: {
        validateAcuLimitLetterStart(v, acuLetterLimitEnd) {
            return moment(v).utcOffset('+0800').startOf('day').isSameOrBefore(moment(acuLetterLimitEnd).utcOffset('+0800').startOf('day')) || `Must not after ACU Provision End Date: ${moment(acuLetterLimitEnd).utcOffset('+0800').startOf('day').format('DD MMM YYYY')}`
        },
        validateAcuLimitLetterEnd(v, acuLetterLimitStart) {
            return moment(v).utcOffset('+0800').startOf('day').isSameOrAfter(moment(acuLetterLimitStart).utcOffset('+0800').startOf('day')) || `Must not before ACU Provision Start Date: ${moment(acuLetterLimitStart).utcOffset('+0800').startOf('day').format('DD MMM YYYY')}`
        },
        validateAcuStart(v, endDate) {
            return moment(v).utcOffset('+0800').startOf('day').isSameOrBefore(moment(endDate).utcOffset('+0800').startOf('day')) || 'Must be same or before ACU End Date'
        },
        validateAcuEnd(v, startDate) {
            return moment(v).utcOffset('+0800').startOf('day').isSameOrAfter(moment(startDate).utcOffset('+0800').startOf('day')) || 'Must be same or after ACU Start Date'
        },
        async prefillFieldsWithSuggestions() {
            let suggestion = this.selectedClinic ? (await AdminAPI.suggestEndorsementRecipient({ MSU_C: this.selectedClinic.MSU_C })).data : null
            suggestion = suggestion && suggestion.data ? suggestion.data : null
            if (suggestion && typeof suggestion === 'object' && Object.keys(suggestion).length > 0) {
                this.endorsementData.TO_PREFIX_END = suggestion.PREFIX
                this.endorsementData.TO_FNAME_END = suggestion.FNAME
                this.endorsementData.TO_SNAME_END = suggestion.SNAME
                this.endorsementData.TO_SUFFIX_END = suggestion.SUFFIX
                this.endorsementData.TO_DESIGNATION_END = suggestion.DESIGNATION
                this.endorsementData.ACU_SCHEDULE = suggestion.ACU_SCHEDULE
                this.endorsementData.ACU_CONTACT_PERSON = suggestion.ACU_CONTACT_PERSON
                this.endorsementData.ACU_CLINIC_TELNO = suggestion.ACU_CLINIC_TELNO
            } else {
                this.endorsementData.TO_PREFIX_END = null
                this.endorsementData.TO_FNAME_END = null
                this.endorsementData.TO_SNAME_END = null
                this.endorsementData.TO_SUFFIX_END = null
                this.endorsementData.TO_DESIGNATION_END = null
                this.endorsementData.ACU_SCHEDULE = null
                this.endorsementData.ACU_CONTACT_PERSON = null
                this.endorsementData.ACU_CLINIC_TELNO = null
            }
        },
        async updateClinicData() {
            this.endorsementData.ACU_CLINIC = this.selectedClinic && this.selectedClinic.MSU_N // Choose from View Account Clinics
            this.endorsementData.ACU_MSU_CODE = this.selectedClinic && this.selectedClinic.MSU_C // Choose from View Account Clinics\
            await this.prefillFieldsWithSuggestions()
        }
    }
}
</script>
